import React, { useState, useEffect, forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { fontStyle, spacing, radius, themeLight } from '../../../theme/index';
export const Input = forwardRef(({ as = 'input', value = '', maxCharacters, onChange, error = false, placeholder, disabled = false, ariaLabel, width = 256, className, ...rest }, ref) => {
    const [count, setCount] = useState(0);
    const [text, setText] = useState(value);
    const handleChange = (e) => {
        if (!onChange)
            return;
        if (maxCharacters && e.target.value.length <= maxCharacters) {
            setCount(e.target.value.length);
            setText(e.target.value);
            onChange(e.target.value);
        }
        if (!maxCharacters) {
            setText(e.target.value);
            onChange(e.target.value);
        }
    };
    useEffect(() => {
        if (maxCharacters && value.length <= maxCharacters) {
            setCount(value.length);
            setText(value);
        }
        if (maxCharacters && value.length > maxCharacters) {
            setText(value.slice(0, maxCharacters));
            setCount(value.slice(0, maxCharacters).length);
        }
        if (!maxCharacters) {
            setText(value);
        }
    }, [value, maxCharacters]);
    return (<Container className={className} width={width}>
        <StyledInput as={as} value={text} placeholder={placeholder} error={error} disabled={disabled} onChange={handleChange} aria-label={ariaLabel} ref={ref} {...rest}/>
        {maxCharacters && (<Count>
            {count}/{maxCharacters} tegn
          </Count>)}
      </Container>);
});
export default Input;
const errorStyle = css `
  border: 2px solid;
  border-color: ${({ theme }) => theme.componentColors ? theme.componentColors.alert.error : themeLight.componentColors.alert.error};
  padding: calc(${spacing.space8} - 1px); // Consistent layout with 2px border
`;
const Container = styled.div `
  display: flex;
  flex-direction: column;
  position: relative;
  width: ${({ width }) => typeof width === 'number' ? `${width}px` : width};
`;
const StyledInput = styled.input `
  background: ${({ theme }) => (theme.background ? theme.background.default : themeLight.background.default)};
  border: 1px solid;
  border-color: ${({ theme }) => (theme.border ? theme.border.heavy : themeLight.border.heavy)};
  box-sizing: border-box;
  border-radius: ${radius.s};
  padding: ${spacing.space8};
  ${fontStyle.article.articleText};
  outline: 0;
  font-variant-numeric: lining-nums !important;

  &:disabled {
    background-color: ${({ theme }) => theme.componentColors
    ? theme.componentColors.inputElement.backgroundDisabled
    : themeLight.componentColors.inputElement.backgroundDisabled};
    border-color: ${({ theme }) => (theme.border ? theme.border.heavy : themeLight.border.heavy)};
    color: ${({ theme }) => (theme.typography ? theme.typography.disabledText : themeLight.typography.disabledText)};
    -webkit-text-fill-color: ${({ theme }) => theme.typography ? theme.typography.disabledText : themeLight.typography.disabledText};
    cursor: not-allowed;
    opacity: 1;
  }

  &:focus {
    border-color: ${({ theme }) => theme.typography ? theme.typography.defaultText : themeLight.typography.defaultText};
  }

  ${({ as }) => as === 'textarea' &&
    css `
      min-height: ${spacing.space160};
    `}

  ${({ as }) => as === 'input' &&
    css `
      height: ${spacing.space48};
    `}

  ::placeholder {
    color: ${({ theme }) => (theme.typography ? theme.typography.subtleText : themeLight.typography.subtleText)};
  }

  ${({ disabled }) => disabled &&
    css `
      background-color: ${({ theme }) => theme.componentColors
        ? theme.componentColors.inputElement.backgroundDisabled
        : themeLight.componentColors.inputElement.backgroundDisabled};
      border-color: ${({ theme }) => (theme.border ? theme.border.heavy : themeLight.border.heavy)};
      color: ${({ theme }) => (theme.typography ? theme.typography.disabledText : themeLight.typography.disabledText)};
      -webkit-text-fill-color: ${({ theme }) => theme.typography ? theme.typography.disabledText : themeLight.typography.disabledText};
      cursor: not-allowed;
      opacity: 1;
    `}

  ${({ error }) => error &&
    css `
      ${errorStyle};
    `}

  &:invalid {
    ${errorStyle};
  }
`;
const Count = styled.span `
  ${fontStyle.bodyText.small};
  display: flex;
  align-self: flex-end;
  bottom: calc(
    -${fontStyle.bodyText.small['font-size']} * ${fontStyle.bodyText.small['line-height']} - ${spacing.space8}
  );
  color: ${({ theme }) => (theme.typography ? theme.typography.subtleText : themeLight.typography.subtleText)};
`;
